import React, { useRef, useState } from 'react';
import { useDocumentApi } from '../../features/auth/hooks/use-document-api.ts';
import '../../scss/components/Documentcenter.scss';

const STREAMABLE_EXTENSIONS = ['mp4', 'webm', 'ogg'];
const VIDEO_EXTENSIONS = ['mp4', 'webm', 'ogg', 'mov', 'avi', 'mkv'];

export const isVideoFile = (filePath: string): boolean => {
    const extension = filePath.split('.').pop()?.toLowerCase();
    return extension ? VIDEO_EXTENSIONS.includes(extension) : false;
};

const isStreamable = (filePath: string): boolean => {
    const extension = filePath.split('.').pop()?.toLowerCase();
    return extension ? STREAMABLE_EXTENSIONS.includes(extension) : false;
};

const VideoItem = ({ item }) => {
    const { getVideoStreamUrl } = useDocumentApi();
    const videoRef = useRef<HTMLVideoElement>(null);
    const [videoSrc, setVideoSrc] = useState<string | null>(null);
    const [showModal, setShowModal] = useState(false);
    const [error, setError] = useState(false);
    const [downloading, setDownloading] = useState(false);

    const downloadVideo = async () => {
        if (downloading) return;
        setDownloading(true);
        try {
            const url = await getVideoStreamUrl(item[1].file_name);
            if (!url) {
                setError(true);
                return;
            }
            const link = document.createElement('a');
            link.href = url;
            link.download = item[1].name;
            link.click();
            link.remove();
        } catch {
            setError(true);
        } finally {
            setDownloading(false);
        }
    };

    const openVideo = async () => {
        if (!isStreamable(item[1].file_name)) {
            await downloadVideo();
            return;
        }

        if (videoSrc) {
            setShowModal(true);
            return;
        }

        setError(false);

        try {
            const url = await getVideoStreamUrl(item[1].file_name);
            if (!url) {
                setError(true);
                return;
            }
            setVideoSrc(url);
            setShowModal(true);
        } catch {
            setError(true);
        }
    };

    const handleVideoError = () => {
        setShowModal(false);
        setVideoSrc(null);
        setError(true);
    };

    const closeModal = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) {
            handleClose();
        }
    };

    const handleClose = () => {
        setShowModal(false);
        videoRef.current?.pause();
    };

    return (
        <div className="video-item">
            <button
                className={`item downloadable-file video${downloading ? ' loading' : ''}`}
                onClick={openVideo}
            >
                <div className="file-info">
                    <span className="filetitle">{item[1].name}</span>
                    {item[1].description ? <span className="filedescription">- {item[1].description}</span> : ''}
                </div>
                {downloading && <div className="loader"></div>}
            </button>
            {error && <p className="video-error">Video konnte nicht gestreamt werden.</p>}
            {showModal && videoSrc && (
                <div className="video-modal" onClick={closeModal}>
                    <div className="video-modal-inner" role="dialog" aria-modal="true">
                        <div className="video-modal-header">
                            <p className="video-modal-title">{item[1].name}</p>
                            <button className="video-modal-close" onClick={handleClose} aria-label="Schließen">&times;</button>
                        </div>
                        <video ref={videoRef} controls autoPlay src={videoSrc} onError={handleVideoError} />
                    </div>
                </div>
            )}
        </div>
    );
};

export default VideoItem;
